
import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { 
    Users, 
    Mountain, 
    DollarSign, 
    TrendingUp, 
    ArrowUpRight,
    ArrowDownRight,
    Calendar,
    CheckCircle2,
    Clock,
    AlertCircle
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { sortExpeditions } from '../../lib/expeditionOrder';

export const BUILD_INFO = {
    version: 'v2.5.2',
    fecha: '11 de sept de 2026 | 11:35 hrs'
};

const Dashboard: React.FC = () => {
    const { theme, expeditions } = useOutletContext<{ theme: 'light' | 'dark'; expeditions: any[] }>();
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    const [bookings, setBookings] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const { data, error } = await supabase
                    .from('bookings')
                    .select('*')
                    .order('created_at', { ascending: false });
                if (error) throw error;
                if (data) {
                    setBookings(data);
                }
            } catch (err) {
                console.error('Error fetching dashboard bookings:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, []);

    // Compute stats from real bookings only (no fallback mock data)
    const stats = React.useMemo(() => {
        const totalCount = bookings.length;
        
        const activeBookings = bookings.filter(b => b.payment_status !== 'cancelled');
        const revenue = activeBookings.reduce((sum, b) => sum + (b.total_price || 0), 0);

        const uniqueExpeditionsCount = new Set(bookings.map(b => b.expedition_slug)).size;

        const confirmedCount = bookings.filter(b =>
            ['paid', 'confirmed', 'completed', 'partially_paid'].includes(b.payment_status)
        ).length;

        return [
            { label: 'Reservas Totales', value: totalCount.toString(), icon: Users, change: loading ? '...' : 'Real', trend: 'neutral' },
            { label: 'Expediciones Solicitadas', value: uniqueExpeditionsCount.toString(), icon: Mountain, change: loading ? '...' : 'Real', trend: 'neutral' },
            { label: 'Ingresos Totales', value: `USD$${revenue.toLocaleString()}`, icon: DollarSign, change: loading ? '...' : 'Real', trend: 'neutral' },
            { label: 'Confirmadas / Pagadas', value: confirmedCount.toString(), icon: TrendingUp, change: loading ? '...' : 'Real', trend: 'neutral' },
        ];
    }, [bookings, loading]);

    // Recent 5 bookings
    const displayBookings = React.useMemo(() => {
        if (loading || bookings.length === 0) {
            return [];
        }
        return bookings.slice(0, 5).map(b => {
            let customer = 'Cliente';
            if (b.customer_data?.lead_name) {
                customer = b.customer_data.lead_name;
            } else if (b.customer_data?.participants?.[0]?.name) {
                customer = b.customer_data.participants[0].name;
            } else if (b.customer_data?.email) {
                customer = b.customer_data.email.split('@')[0];
            } else if (b.participants?.[0]?.fullName) {
                customer = b.participants[0].fullName;
            } else if (b.contact_email) {
                customer = b.contact_email;
            } else {
                customer = 'Sin Nombre';
            }

            const exp = (expeditions || []).find(e => e.id === b.expedition_slug || e.slug === b.expedition_slug);
            const expName = exp ? (exp.name_es || exp.name_en) : (b.expedition_slug || '—');

            let statusLabel = 'Pendiente';
            if (b.payment_status === 'partially_paid') statusLabel = 'Abonado 50%';
            else if (b.payment_status === 'paid') statusLabel = 'Pagado';
            else if (b.payment_status === 'confirmed') statusLabel = 'Confirmado';
            else if (b.payment_status === 'completed') statusLabel = 'Completado';
            else if (b.payment_status === 'cancelled') statusLabel = 'Cancelado';

            return {
                id: b.id,
                customer,
                exp: expName,
                date: b.start_date ? new Date(b.start_date).toLocaleDateString('es-CL') : '—',
                status: statusLabel,
                amount: `USD$${Number(b.total_price || 0).toLocaleString()}`
            };
        });
    }, [bookings, loading, expeditions]);

    // Active expeditions from context (real data from supabase)
    const activeExpeditions = React.useMemo(() => {
        return sortExpeditions(expeditions || [])
            .filter((e: any) => e.status === 'active' || e.is_visible)
            .slice(0, 5)
            .map((e: any) => ({
                id: e.id,
                name: e.name_es || e.name_en || '—',
                status: e.status === 'active' ? 'Activa' : 'En Desarrollo',
                difficulty: e.difficulty || 'mid',
                color: e.status === 'active' ? '#00d4ff' : '#f2f2f2'
            }));
    }, [expeditions]);

    return (
        <div className="space-y-10 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex flex-col gap-2">
                    <h1 className={`text-4xl md:text-5xl font-title uppercase tracking-tighter ${isDark ? 'text-white' : 'text-gray-900'}`}>Resumen General</h1>
                    <p className="text-gray-400 text-xs uppercase tracking-[0.3em] font-bold">Bienvenido de nuevo, así va Fisura Expeditions hoy.</p>
                </div>

                {/* Build Info Badge (Fecha y Versión como en FADECH) */}
                <div className={`flex items-center gap-2.5 px-4 py-2.5 border rounded-[10px] text-xs font-medium self-start md:self-auto transition-all ${
                    isDark ? 'bg-white/5 border-rock text-gray-300' : 'bg-white border-gray-200 text-gray-700 shadow-sm'
                }`}>
                    <Clock className="w-4 h-4 text-[#00d4ff]" />
                    <span>Fecha última actualización: <strong className={isDark ? 'text-white' : 'text-gray-900'}>{BUILD_INFO.fecha}</strong></span>
                    <span className="text-[10px] font-bold bg-[#00d4ff]/10 text-[#00d4ff] border border-[#00d4ff]/30 px-2 py-0.5 rounded tracking-wider ml-1">
                        {BUILD_INFO.version}
                    </span>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, i) => (
                    <div key={i} className={`border p-6 rounded-[10px] relative overflow-hidden group transition-all duration-300 ${
                        isDark ? 'bg-[#1a1512] border-rock hover:border-[#00d4ff]/50' : 'bg-white border-gray-200 hover:border-[#00d4ff] shadow-sm'
                    }`}>
                        <div className={`absolute -right-4 -top-4 w-24 h-24 rounded-[10px] blur-2xl transition-colors ${
                            isDark ? 'bg-white/5 group-hover:bg-[#00d4ff]/10' : 'bg-gray-100 group-hover:bg-[#00d4ff]/5'
                        }`}></div>
                        
                        <div className="flex justify-between items-start mb-4 relative z-10">
                            <div className={`w-10 h-10 border rounded-[10px] flex items-center justify-center transition-all ${
                                isDark ? 'bg-black border-rock text-gray-400 group-hover:text-[#00d4ff] group-hover:border-[#00d4ff]/50' : 'bg-gray-50 border-gray-200 text-gray-500 group-hover:text-[#00d4ff] group-hover:border-[#00d4ff]'
                            }`}>
                                <stat.icon className="w-5 h-5 font-bold" style={{ color: isDark ? '#00d4ff' : '#0072ff' }} />
                            </div>
                            <div className={`flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest ${
                                stat.trend === 'up' ? 'text-[#25D366]' : stat.trend === 'down' ? 'text-red-500' : 'text-gray-500'
                            }`}>
                                {stat.trend === 'up' && <ArrowUpRight className="w-3 h-3" />}
                                {stat.trend === 'down' && <ArrowDownRight className="w-3 h-3" />}
                                {stat.change}
                            </div>
                        </div>

                        <div className="relative z-10">
                            <p className="text-[9px] text-gray-500 font-bold uppercase tracking-[0.2em] mb-1">{stat.label}</p>
                            <p className={`text-2xl font-title tracking-tighter ${isDark ? 'text-white' : 'text-gray-900'}`}>{stat.value}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Recent Bookings */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="flex justify-between items-center">
                        <h3 className={`text-xl font-title uppercase tracking-widest flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                            <span className="w-2 h-2 bg-[#00d4ff] block"></span>
                            Reservas Recientes
                        </h3>
                        <button onClick={() => navigate('/admin/bookings')} className="text-[10px] font-bold text-[#00d4ff] uppercase tracking-widest hover:text-white transition-colors">Ver todas →</button>
                    </div>

                    <div className={`border overflow-hidden rounded-[10px] ${isDark ? 'bg-[#1a1512] border-rock' : 'bg-white border-gray-200 shadow-sm'}`}>
                        {loading ? (
                            <div className="flex items-center justify-center py-12 text-gray-500 text-xs font-bold uppercase tracking-widest">
                                Cargando datos...
                            </div>
                        ) : displayBookings.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-14 text-center gap-3">
                                <span className="text-4xl">📋</span>
                                <p className={`text-sm font-bold uppercase tracking-widest ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Sin reservas aún</p>
                                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Las reservas de clientes reales aparecerán aquí</p>
                            </div>
                        ) : (
                            <table className="w-full text-left">
                                <thead>
                                    <tr className={`border-b text-[10px] font-bold uppercase tracking-widest text-gray-500 ${isDark ? 'bg-black/40 border-rock' : 'bg-gray-50 border-gray-100'}`}>
                                        <th className="px-6 py-4">Cliente</th>
                                        <th className="px-6 py-4">Expedición</th>
                                        <th className="px-6 py-4">Fecha</th>
                                        <th className="px-6 py-4">Estado</th>
                                        <th className="px-6 py-4 text-right">Monto</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y text-xs ${isDark ? 'divide-rock/50' : 'divide-gray-100'}`}>
                                    {displayBookings.map((booking) => (
                                        <tr key={booking.id} className={`transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                                            <td className={`px-6 py-4 font-bold uppercase tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>{booking.customer}</td>
                                            <td className="px-6 py-4 text-gray-400 font-light">{booking.exp}</td>
                                            <td className="px-6 py-4 text-gray-500 font-mono">{booking.date}</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-0.5 rounded-[10px] text-[8px] font-bold uppercase tracking-widest border ${
                                                    booking.status === 'Pagado' ? 'bg-[#25D366]/10 text-[#25D366] border-[#25D366]/20' :
                                                    booking.status === 'Confirmado' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                                                    booking.status === 'Completado' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' :
                                                    booking.status === 'Pendiente' ? 'bg-[#00d4ff]/10 text-[#00d4ff] border-[#00d4ff]/20' :
                                                    'bg-red-500/10 text-red-500 border-red-500/20'
                                                }`}>
                                                    {booking.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right font-title text-[#00d4ff]">{booking.amount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* Active Expeditions List */}
                <div className="space-y-6">
                    <div className="flex justify-between items-center">
                        <h3 className={`text-xl font-title uppercase tracking-widest flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                            <span className="w-2 h-2 bg-[#25D366] block"></span>
                            Próximas Salidas
                        </h3>
                    </div>

                    <div className="space-y-4">
                        {activeExpeditions.map((exp) => (
                            <div key={exp.id} className={`border p-5 rounded-[10px] group transition-all ${
                                isDark ? 'bg-[#1a1512] border-rock hover:border-[#00d4ff]/30' : 'bg-white border-gray-200 hover:border-[#00d4ff] shadow-sm'
                            }`}>
                                <div className="flex justify-between items-start mb-3">
                                    <span className="text-[9px] font-bold text-[#00d4ff] uppercase tracking-widest">Trekking</span>
                                    <span className={`text-[8px] font-bold uppercase tracking-widest flex items-center gap-1.5 ${
                                        exp.status === 'Activa' ? 'text-[#25D366]' : 'text-gray-500'
                                    }`}>
                                        {exp.status === 'Activa' ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                        {exp.status}
                                    </span>
                                </div>
                                <h4 className={`text-sm font-bold uppercase tracking-tight mb-4 transition-colors ${
                                    isDark ? 'text-white group-hover:text-[#00d4ff]' : 'text-gray-900 group-hover:text-[#00d4ff]'
                                }`}>{exp.name}</h4>
                                <div className={`flex items-center justify-between pt-4 border-t ${isDark ? 'border-rock/50' : 'border-gray-100'}`}>
                                    <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase font-bold tracking-widest">
                                        <Calendar className="w-3 h-3" />
                                        <span>15 Mayo</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase font-bold tracking-widest">
                                        <Users className="w-3 h-3" />
                                        <span>4/6</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                        
                        <div className={`border border-dashed p-6 text-center space-y-3 rounded-[10px] ${isDark ? 'border-rock' : 'border-gray-300'}`}>
                            <AlertCircle className="w-6 h-6 text-gray-600 mx-auto" />
                            <p className="text-[10px] text-gray-600 font-bold uppercase tracking-widest">Sin más salidas confirmadas</p>
                            <button className="text-[10px] font-bold text-[#00d4ff] uppercase tracking-widest hover:underline">Configurar nueva fecha</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
